import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material'
import { LuminaMemberEvaluationHistory } from './services'

interface Props {
  people: LuminaMemberEvaluationHistory[]
  choices: Record<string, number>
  saving: boolean
  onChoiceChange: (key: string, value: number) => void
  onCancel: () => void
  onConfirm: () => void
}

const categories = [
  { id: 1, label: 'BETESA' },
  { id: 2, label: '3D Skills' },
]

const formatDate = (value: string | null): string => value
  ? String(value).slice(0, 10).split('-').reverse().join('/') : 'Sin fecha'

const ManualEvaluationSelectionDialog = ({
  people, choices, saving, onChoiceChange, onCancel, onConfirm,
}: Props) => {
  const allSelected = people.every((person) => categories.every((category) => {
    const evaluations = person.evaluations.filter((item) => item.category_id === category.id)
    return evaluations.length <= 1 || Boolean(choices[`${person.user_id}:${category.id}`])
  }))

  return (
    <Dialog open onClose={() => { if (!saving) onCancel() }} fullWidth maxWidth="md">
      <DialogTitle>Elegir pruebas para el equipo</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" marginBottom={2}>
          Elige las pruebas que se asociarán al equipo y se enviarán a Lumina. Cada persona aparece una sola vez;
          las categorías con una única prueba se seleccionan automáticamente.
        </Typography>
        {people.map((person) => (
          <Box
            key={person.user_id}
            sx={{ mb: 2, p: 2, border: '1px solid #d9e3ea', borderRadius: 2, backgroundColor: '#f8fbfd' }}
          >
            <Typography component="h3" fontWeight={700} color="#17374D" marginBottom={1.5}>
              {person.full_name}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
              {categories.map((category) => {
                const evaluations = person.evaluations.filter((item) => item.category_id === category.id)
                if (!evaluations.length) return null
                const key = `${person.user_id}:${category.id}`
                const single = evaluations.length === 1
                return (
                  <Box key={category.id}>
                    <FormControl fullWidth size="small" sx={{ backgroundColor: '#fff' }}>
                      <InputLabel id={`evaluation-${key}`}>{category.label}</InputLabel>
                      <Select
                        labelId={`evaluation-${key}`}
                        label={category.label}
                        value={single ? evaluations[0].user_test_id : choices[key] || ''}
                        disabled={saving}
                        onChange={(event) => { if (!single) onChoiceChange(key, Number(event.target.value)) }}
                      >
                        {evaluations.map((evaluation) => (
                          <MenuItem key={evaluation.user_test_id} value={evaluation.user_test_id}>
                            {evaluation.process_name} · {formatDate(evaluation.answer_date)} · #{evaluation.user_test_id}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {single && (
                      <Typography variant="caption" color="text.secondary">
                        Única prueba disponible; se asociará automáticamente.
                      </Typography>
                    )}
                  </Box>
                )
              })}
            </Box>
          </Box>
        ))}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button disabled={saving} onClick={onCancel}>Cancelar</Button>
        <Button
          variant="contained"
          disabled={saving || !allSelected}
          onClick={onConfirm}
          sx={{ borderRadius: '12px', background: 'linear-gradient(135deg, #c6016b 0%, #8f215d 100%)',
            color: '#fff', fontWeight: 700, textTransform: 'none' }}
        >
          {saving ? 'Guardando…' : 'Asociar pruebas y guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ManualEvaluationSelectionDialog
